import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { IxmUserauthV1ProgramContacts } from '@interxfi/types/generated/api';
import type { FormConfirmationGroup } from '../../../../../components/form/confirmation/form-confirmation';
import { addDefinedValues } from '../../../../../util/has-value';
import { getProgramContactListItems, getProgramContactsListItems } from '../../program-list-items';
import {
  getInitialFormValuesForOptionalProgramContactGroup,
  OptionalProgramContactGroupForm,
  optionalFormValuesToProgramContactGroup,
  optionalProgramContactGroupSchema,
} from './optional-program-contact-group-form';
import {
  defaultProgramContactFormGroupValues,
  formValuesToProgramContactGroup,
  getInitialFormValuesForProgramContactGroup,
  getProgramContactGroupSchema,
  ProgramContactGroupForm,
} from './program-contact-group-form';
import styles from './program-contacts-form.module.scss';

export const programContactsFormSchema = z.object({
  executive: getProgramContactGroupSchema('Executive'),
  technical: getProgramContactGroupSchema('Technical'),
  billing: getProgramContactGroupSchema('Billing'),
  operations: getProgramContactGroupSchema('Operations'),
  compliance: optionalProgramContactGroupSchema,
  disasterRecovery: optionalProgramContactGroupSchema,
  aml: optionalProgramContactGroupSchema,
  cat: optionalProgramContactGroupSchema,
});

export type ProgramContactsFormValues = z.infer<typeof programContactsFormSchema>;

export const defaultProgramContactsFormValues: ProgramContactsFormValues = {
  executive: defaultProgramContactFormGroupValues,
  technical: defaultProgramContactFormGroupValues,
  billing: defaultProgramContactFormGroupValues,
  operations: defaultProgramContactFormGroupValues,
  compliance: { isProviding: false, value: [] },
  disasterRecovery: { isProviding: false, value: [] },
  aml: { isProviding: false, value: [] },
  cat: { isProviding: false, value: [] },
};

export function getInitialFormValuesForProgramContacts(data: IxmUserauthV1ProgramContacts | undefined): ProgramContactsFormValues {
  if (!data) {
    return defaultProgramContactsFormValues;
  }

  return {
    ...defaultProgramContactFormGroupValues,
    executive: getInitialFormValuesForProgramContactGroup(data.executive),
    technical: getInitialFormValuesForProgramContactGroup(data.technical),
    billing: getInitialFormValuesForProgramContactGroup(data.billing),
    operations: getInitialFormValuesForProgramContactGroup(data.operations),
    compliance: getInitialFormValuesForOptionalProgramContactGroup(data.compliance),
    disasterRecovery: getInitialFormValuesForOptionalProgramContactGroup(data.disasterRecovery),
    aml: getInitialFormValuesForOptionalProgramContactGroup(data.aml),
    cat: getInitialFormValuesForOptionalProgramContactGroup(data.cat),
  };
}

export function formValuesToProgramContacts(values: ProgramContactsFormValues): IxmUserauthV1ProgramContacts {
  return addDefinedValues<IxmUserauthV1ProgramContacts>(
    {
      executive: formValuesToProgramContactGroup(values.executive)!,
      technical: formValuesToProgramContactGroup(values.technical)!,
      billing: formValuesToProgramContactGroup(values.billing)!,
      operations: formValuesToProgramContactGroup(values.operations)!,
    },
    {
      compliance: optionalFormValuesToProgramContactGroup(values.compliance),
      disasterRecovery: optionalFormValuesToProgramContactGroup(values.disasterRecovery),
      aml: optionalFormValuesToProgramContactGroup(values.aml),
      cat: optionalFormValuesToProgramContactGroup(values.cat),
    },
  );
}

const emptyContactListItems = getProgramContactListItems(undefined);

function getContactGroup(id: string, title: string, contacts: ReturnType<typeof getProgramContactListItems>[] | undefined): FormConfirmationGroup {
  const contactItems = contacts?.length ? contacts : [emptyContactListItems];

  return {
    id,
    title,
    listGap: 0,
    list: contactItems.map((contact, index) => ({
      id: String(index),
      items: [contact.nameAndTitle, contact.email, contact.phone],
    })),
  };
}

export function mapProgramContactsToFormConfirmationGroups(values: IxmUserauthV1ProgramContacts | undefined): FormConfirmationGroup[] {
  const items = getProgramContactsListItems(values);

  return [
    getContactGroup('executive', 'Executive', items.executive),
    getContactGroup('technical', 'Technical', items.technical),
    getContactGroup('billing', 'Billing', items.billing),
    getContactGroup('operations', 'Operations', items.operations),
    getContactGroup('compliance', 'Compliance', items.compliance),
    getContactGroup('disasterRecovery', 'Disaster Recovery', items.disasterRecovery),
    getContactGroup('aml', 'AML', items.aml),
    getContactGroup('cat', 'CAT', items.cat),
  ];
}

interface ProgramContactsFormProps {
  lens: Lens<ProgramContactsFormValues>;
}

export function ProgramContactsForm({ lens }: ProgramContactsFormProps) {
  return (
    <VerticalStack className={styles.programContactsForm}>
      <ProgramContactGroupForm appendLabel="Add an Executive Contact" label="Executive Contact" lens={lens.focus('executive')} />

      <ProgramContactGroupForm appendLabel="Add a Technical Contact" label="Technical Contact" lens={lens.focus('technical')} />

      <ProgramContactGroupForm appendLabel="Add a Billing Contact" label="Billing Contact" lens={lens.focus('billing')} />

      <ProgramContactGroupForm appendLabel="Add an Operations Contact" label="Operations Contact" lens={lens.focus('operations')} />

      <OptionalProgramContactGroupForm appendLabel="Add a Compliance Contact" label="Compliance Contact" lens={lens.focus('compliance')} />

      <OptionalProgramContactGroupForm
        appendLabel="Add a Disaster Recovery Contact"
        label="Disaster Recovery Contact"
        lens={lens.focus('disasterRecovery')}
      />

      <OptionalProgramContactGroupForm appendLabel="Add an AML Contact" label="AML Contact" lens={lens.focus('aml')} />

      <OptionalProgramContactGroupForm appendLabel="Add a CAT Contact" label="CAT Contact" lens={lens.focus('cat')} />
    </VerticalStack>
  );
}
